/**
 * Industry editor — shared guidance + peer-hint primitives.
 *
 * TabGuidance is the collapsible amber/sky banner shown at the top of
 * every editor tab. PeerHint is the per-input line that surfaces
 * "Peers: <min>–<max> · avg <avg> (n=<N>)" plus an inline
 * "Outside peer range" warning when the draft value drifts.
 */
import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { fmtCompact } from './helpers';

export function TabGuidance({ title, what, affects, realistic, testId }) {
  const [open, setOpen] = useState(true);
  return (
    <div
      className="rounded-lg border border-sky-200 bg-sky-50/60 px-3 py-2.5 text-xs text-sky-900"
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 text-left"
      >
        <span className="inline-flex items-center gap-1.5 font-semibold">
          <AlertCircle className="h-3.5 w-3.5 text-sky-600" />
          {title}
        </span>
        <span className="text-[10px] text-sky-700 underline">{open ? 'Hide guidance' : 'Show guidance'}</span>
      </button>
      {open && (
        <div className="mt-2 space-y-1.5 leading-relaxed">
          <div>
            <span className="font-semibold uppercase text-[9px] tracking-wider text-sky-700">What this is</span>{' '}
            {what}
          </div>
          <div>
            <span className="font-semibold uppercase text-[9px] tracking-wider text-sky-700">What it affects</span>{' '}
            {affects}
          </div>
          {realistic && (
            <div>
              <span className="font-semibold uppercase text-[9px] tracking-wider text-sky-700">Realistic values</span>{' '}
              {realistic}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function PeerHint({ stats, currentValue, prefix = '', suffix = '', testId, formatter = fmtCompact }) {
  if (!stats) {
    // Render a stub even when no peers have set this field yet, so the
    // hint slot is consistently present in the DOM and admins know the
    // absence is intentional rather than a bug.
    return (
      <div className="mt-1 text-[10px] text-slate-400 italic" data-testid={testId}>
        No peer data yet — you&apos;ll set the bar.
      </div>
    );
  }
  const num = currentValue === '' || currentValue == null ? null : Number(currentValue);
  const outOfRange =
    num != null && !Number.isNaN(num) && (num < stats.min || num > stats.max);
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px]" data-testid={testId}>
      <span className="text-slate-500 font-mono">
        Peers: {prefix}{formatter(stats.min)}{suffix}–{prefix}{formatter(stats.max)}{suffix}
        {' '}· avg {prefix}{formatter(stats.avg)}{suffix}
        {' '}<span className="text-slate-400">(n={stats.n})</span>
      </span>
      {outOfRange && (
        <span className="inline-flex items-center gap-0.5 text-amber-700 font-semibold">
          <AlertCircle className="h-2.5 w-2.5" />
          Outside peer range
        </span>
      )}
    </div>
  );
}
